import type { Middleware, UnknownAction } from '@reduxjs/toolkit';
import { TagTypes } from '../../Api/Types/tagTypes';
import { bookingsApi } from '../../Features/Bookings';
import { bankDocumentsApi } from '../../Features/BankDocuments';
import { reservationsApi } from '../../Features/Reservation';

const normalizeBookingIds = (ids: readonly number[] | null | undefined): number[] =>
  Array.isArray(ids)
    ? Array.from(new Set(ids.filter((id): id is number => Number.isInteger(id) && id > 0)))
    : [];

const shouldRefreshReservationPaymentViews = (action: UnknownAction): boolean =>
  bankDocumentsApi.endpoints.postBankDocumentEntry.matchFulfilled(action) ||
  bankDocumentsApi.endpoints.unpostBankDocumentEntry.matchFulfilled(action) ||
  bankDocumentsApi.endpoints.postBankDocumentBatch.matchFulfilled(action);

const getAffectedBookingIds = (action: UnknownAction): number[] => {
  const payload = (action as { payload?: { affectedBookingIds?: readonly number[] | null } }).payload;
  return normalizeBookingIds(payload?.affectedBookingIds);
};

export const bankDocumentsReservationRefreshMiddleware: Middleware =
  (storeApi) => (next) => (action) => {
    const result = next(action);

    if (!shouldRefreshReservationPaymentViews(action as UnknownAction)) {
      return result;
    }

    storeApi.dispatch(
      reservationsApi.util.invalidateTags([
        { type: TagTypes.Reservation, id: TagTypes.List },
        { type: TagTypes.ReservationPayment, id: TagTypes.List },
      ])
    );

    const affectedBookingIds = getAffectedBookingIds(action as UnknownAction);
    if (affectedBookingIds.length > 0) {
      storeApi.dispatch(
        bookingsApi.util.invalidateTags(
          affectedBookingIds.map((id) => ({ type: TagTypes.Booking as const, id }))
        )
      );
    }

    return result;
  };
